import { createTheme, alpha } from "@mui/material/styles";
import { tokens } from "./tokens";
import { cssTransition } from "@/components/Chat/motion";

// Add a brand `tertiary` slot to MUI's palette so components can read
// theme.palette.tertiary.main (terracotta) the same way they read primary.
declare module "@mui/material/styles" {
  interface Palette {
    tertiary: Palette["primary"];
  }
  interface PaletteOptions {
    tertiary?: PaletteOptions["primary"];
  }
}
// Allow `color="tertiary"` on Buttons / Chips so CTAs can use the terracotta.
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiary: true;
  }
}
declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    tertiary: true;
  }
}

// Graphite surfaces (neutral light) — kept in sync with the @theme block in
// styles/theme.css.
const surface = { default: "#f6f7f8", paper: "#ffffff", subtle: "#eef0f2" };
const ink = {
  primary: "#16181b",
  secondary: "#565b63",
  disabled: "#a9adb4",
};
const divider = "#e5e7ea";

/** Build the light MUI theme with `primary` = the active accent (a real hex,
 *  so MUI's alpha()/contrast math keeps working). AppearanceProvider passes
 *  the user's chosen accent (or the preset default) so every MUI control —
 *  buttons, tabs, selected/hover states — follows the same accent as the
 *  Tailwind side. Defaults to the sage accent for the bare `lightTheme` export
 *  (tests + any direct import). */
export function makeLightTheme(primary: string = tokens.brand.primary) {
  return createTheme({
  palette: {
    mode: "light",
    background: { default: surface.default, paper: surface.paper },
    primary: { main: primary, contrastText: tokens.brand.primaryContrast },
    secondary: { main: tokens.brand.secondary, contrastText: tokens.brand.secondaryContrast },
    // Terracotta tertiary — the warm counterpoint, used for accents/badges.
    // Augmented onto Palette via the module declaration at the bottom.
    tertiary: { main: tokens.brand.tertiary, contrastText: tokens.brand.tertiaryContrast },
    text: { primary: ink.primary, secondary: ink.secondary, disabled: ink.disabled },
    divider,
    error: { main: "#d2544f" },
    success: { main: primary },
    // Warning leans on the terracotta family so caution states feel on-brand.
    warning: { main: tokens.brand.tertiary },
  },
  typography: {
    fontFamily: tokens.fontFamily,
    fontSize: tokens.type.base,
    h5: tokens.type.h5,
    subtitle1: tokens.type.subtitle,
    subtitle2: tokens.type.subtitle,
    body1: { lineHeight: tokens.type.lineHeight },
    body2: { lineHeight: tokens.type.lineHeight },
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: tokens.radius.lg },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "html, body, #root": { height: "100%" },
        "@media (prefers-reduced-motion: no-preference)": {
          "html, body": { scrollBehavior: "smooth" },
        },
        body: {
          // Track the active preset's canvas so every page (including the
          // not-yet-migrated MUI ones) re-skins when the user picks a theme.
          backgroundColor: `var(--color-canvas, ${surface.default})`,
          overflow: "hidden",
          // Flat surface — no atmosphere gradient in the new design.
          backgroundImage: "none",
          color: ink.primary,
        },
        // Branded keyboard focus ring across every focusable control. Mouse
        // clicks don't trigger :focus-visible, so it only shows on Tab nav.
        ".MuiButtonBase-root:focus-visible, a:focus-visible": {
          outline: "none",
          // Keyboard focus ring in the active accent.
          boxShadow: `0 0 0 2px ${surface.default}, 0 0 0 4px ${primary}`,
        },
        "::selection": { background: tokens.brand.primarySoft, color: ink.primary },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": {
          background: "rgba(35,34,31,0.18)", borderRadius: 8,
          border: "2px solid transparent", backgroundClip: "padding-box",
        },
        "*::-webkit-scrollbar-thumb:hover": { background: "rgba(35,34,31,0.30)", backgroundClip: "padding-box" },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // Preset-aware card surface (falls back to sage white).
          backgroundColor: `var(--color-surface, ${surface.paper})`,
          backgroundImage: "none",
          border: `1px solid var(--color-edge, ${divider})`,
          boxShadow: tokens.shadow.sm,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: surface.paper, borderBottom: `1px solid ${divider}`, boxShadow: "none" },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.subtle,
          borderRight: `1px solid ${divider}`,
          boxShadow: "none",
          backgroundImage: "none",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          // Full pill — the pre-refresh shape, and how a labeled CTA button
          // (Upload, Add plugin, Publish plugin…) reads right; the tightened
          // `control` radius is for icon-only/menu hover states instead.
          borderRadius: tokens.radius.pill,
          transition: cssTransition.lift,
          "&:hover": { transform: "translateY(-1px)" },
          "&:active": { transform: "translateY(0)" },
          "@media (prefers-reduced-motion: reduce)": {
            transition: "box-shadow 200ms ease",
            "&:hover": { transform: "none" },
          },
        },
        // Flat filled buttons — no colored glow/halo. A hairline drop shadow
        // gives just enough lift; hover darkens the fill instead of blooming.
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none", filter: "brightness(0.96)" },
        },
        outlined: {
          borderColor: divider,
          "&:hover": { borderColor: "rgba(32,31,28,0.22)", backgroundColor: surface.subtle },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { backgroundColor: surface.subtle, border: `1px solid ${divider}` },
        outlined: { borderColor: divider },
      },
    },
    // Text boxes: a plain 1px border, deepening to a 2px accent ring on focus.
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: surface.paper,
            borderRadius: tokens.radius.md,
            boxShadow: "none",
            transition: "box-shadow 160ms ease, border-color 160ms ease",
            "& fieldset": { borderColor: divider },
            "&:hover fieldset": { borderColor: divider },
            "&.Mui-focused": {
              boxShadow: `0 0 0 2px ${alpha(primary, 0.35)}`,
            },
            "&.Mui-focused fieldset": { borderColor: "transparent" },
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        // Solid accent underline (no gradient) — the active tab uses the same
        // accent as every other control.
        indicator: { height: 3, borderRadius: 2, backgroundColor: primary },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { textTransform: "none", fontWeight: 600, "&.Mui-selected": { color: primary } },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: "rgba(24,26,23,0.92)", color: "#fff", fontSize: 12 },
      },
    },
    // Floating chrome → neumorphic: opaque page-matched surface, soft extruded
    // shadow, no hard border.
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.default,
          backgroundImage: "none",
          border: `1px solid ${divider}`,
          borderRadius: tokens.radius.lg,
          boxShadow: tokens.elevation[3],
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: surface.paper,
          backgroundImage: "none",
          border: `1px solid ${divider}`,
          borderRadius: tokens.radius.md,
          padding: 6,
          boxShadow: tokens.elevation[2],
        },
        list: { padding: 0 },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          margin: "2px 0",
          transition: "background-color 140ms ease",
          "&:hover": { backgroundColor: surface.subtle },
          "&.Mui-selected": {
            backgroundColor: alpha(primary, 0.14),
            "&:hover": { backgroundColor: alpha(primary, 0.2) },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.radius.control,
          transition: "background-color 160ms ease, color 160ms ease",
          "&:hover": { backgroundColor: surface.subtle },
        },
      },
    },
  },
  });
}

/** Default light theme (sage accent) — for tests and any direct import. */
export const lightTheme = makeLightTheme();
